import clsx from 'clsx';
import { FilterDropdown, FilterDropdownVariant } from 'components/FilterDropdown/FilterDropdown';
import { useFiltersModalContext } from 'components/FiltersModalProvider/FiltersModalProvider';
import { useFiltersContext } from 'components/FiltersProvider/FiltersProvider';
import { SearchIcon } from 'components/Icon';
import { DEFAULT_INITIAL_ADULTS, DEFAULT_INITIAL_CHILDREN } from 'components/NumberOfGuests/constants';
import { NumberOfGuestsSelection } from 'components/NumberOfGuests/types';
import { NumberOfGuestsModal } from 'components/NumberOfGuestsModal/NumberOfGuestsModal';
import { BasicButton } from 'components/buttons/BasicButton/BasicButton';
import { useAppContext } from 'contexts/appContext';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useMedia } from 'hooks/useMedia';
import { BrandCode } from 'lib/config/config';
import { getSearchLink } from 'lib/getSearchLink';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { sizes } from 'styles/sizes';
import { HEADER_MARGIN, NUMBER_OF_GUESTS_LOCAL_STORAGE } from '../constants';
import { CruiseSearchWidgetProps, FilterDropdownConfig } from '../types';
import { getSelectionForNumberOfGuests, getTotalSelectionForFilter, isNumberOfGuestsFilter, isUniqueFilter } from '../utils/reducers';
import styles from './HomeCruiseSearchWidget.css';

declare global {
	interface Window {
		utag_data: {
			pageName: string;
		};
	}
}

export function HomeCruiseSearchWidget({ scrollingContainer, ...props }: Readonly<CruiseSearchWidgetProps>) {
	const { enabledFilters, setActiveFilter, activeFilter, historicalSelection, selection, filterKeyToCustomFilterConfig } = useFiltersContext();
	const { setShowFiltersModal, showFiltersModal, numberOfGuestsSelection, setNumberOfGuestsSelection } = useFiltersModalContext();
	const { dictionary, locale, brandCode } = useAppContext();
	const { isMobile, isTabletPortrait } = useBreakpoint();

	const { labelFindACruise, actionSearchCruises } = dictionary;

	const isTabletLandscape = useMedia(`screen and (min-width: ${sizes.tabletLandscape}px)`);

	const numberOfGuestsFilterRef = useRef(null);

	const isOpenModalPinned = useMemo(() => showFiltersModal && !isNumberOfGuestsFilter(activeFilter), [activeFilter, showFiltersModal]);
	const isPinned = useMemo(() => {
		if (isOpenModalPinned) {
			return false;
		}

		return props.isPinned;
	}, [isOpenModalPinned, props.isPinned]);

	const scrollDown = useMemo(() => {
		if (isMobile || isTabletPortrait) return HEADER_MARGIN.mobile;
		else return HEADER_MARGIN.desktop;
	}, [isMobile, isTabletPortrait]);

	const scrollToMove = (current: number) => {
		const scrollToValue = current <= 0 && current >= scrollDown ? scrollDown * -1 : current * -1 + 1;
		window.scrollTo(0, scrollToValue);
	};

	const isPinnedSlim = useMemo(() => !isTabletLandscape && isPinned, [isTabletLandscape, isPinned]);
	const isPinnedRegular = useMemo(() => isTabletLandscape && isPinned, [isTabletLandscape, isPinned]);
	const handleFilterClick = useCallback(
		(filter: FilterDropdownConfig) => {
			setActiveFilter((current) => {
				setShowFiltersModal(filter.key !== current?.key);
				if (brandCode === BrandCode.Celebrity) {
					const currentScroll = document.body.getBoundingClientRect();
					scrollToMove(currentScroll.top);
				}
				return filter.key !== activeFilter?.key ? filter : null;
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[activeFilter?.key, setActiveFilter, setShowFiltersModal],
	);

	const handleSubmitClick = useCallback(() => {
		const searchLink = getSearchLink(selection, numberOfGuestsSelection, window?.utag_data?.pageName, locale.path);
		window.location.href = searchLink.url;
	}, [selection, numberOfGuestsSelection, locale.path]);

	useEffect(() => {
		const uniqueFilter = historicalSelection.find((item) => isUniqueFilter(item));

		if (!uniqueFilter) {
			return;
		}

		const newNumberOfGuestsSelection: NumberOfGuestsSelection = {
			adults: uniqueFilter.criteria?.adults?.[0] ?? numberOfGuestsSelection.adults,
			children: uniqueFilter.criteria?.children?.[0] ?? numberOfGuestsSelection.children,
		};

		setNumberOfGuestsSelection(newNumberOfGuestsSelection);
	}, [historicalSelection, numberOfGuestsSelection?.adults, numberOfGuestsSelection?.children, setNumberOfGuestsSelection]);

	useEffect(() => {
		const isDefaultOccupancy =
			numberOfGuestsSelection.adults === DEFAULT_INITIAL_ADULTS && numberOfGuestsSelection.children === DEFAULT_INITIAL_CHILDREN;

		if (isDefaultOccupancy) {
			localStorage.removeItem(NUMBER_OF_GUESTS_LOCAL_STORAGE.key);
			return;
		}

		const adults = numberOfGuestsSelection.adults ? `${NUMBER_OF_GUESTS_LOCAL_STORAGE.adults}:${numberOfGuestsSelection.adults}` : '';
		const children = numberOfGuestsSelection.children ? `${NUMBER_OF_GUESTS_LOCAL_STORAGE.children}:${numberOfGuestsSelection.children}` : '';

		localStorage.setItem(NUMBER_OF_GUESTS_LOCAL_STORAGE.key, JSON.stringify({ adults, children }));
	}, [numberOfGuestsSelection]);

	return (
		<div className={clsx(styles.root, { [styles.pinned]: isPinned, [styles.openModalPinned]: isOpenModalPinned })}>
			<div className={styles.content}>
				{!isPinned && <div className={styles.title}>{labelFindACruise}</div>}
				<div className={styles.filters}>
					{enabledFilters.map((filter) => {
						const selectedOptions = isNumberOfGuestsFilter(filter)
							? getSelectionForNumberOfGuests(numberOfGuestsSelection, dictionary)
							: getTotalSelectionForFilter(historicalSelection, filter, filterKeyToCustomFilterConfig, props.enableUniqueFilters);

						return (
							<div className={styles.filter} key={filter.label.replace(/ /g, '_').toLowerCase()}>
								<p className={styles.label}>{dictionary[filter.dictionaryTopLabel ?? '']}</p>
								<FilterDropdown
									filterKey={filter.key}
									testId={filter.desktopTestId}
									label={dictionary[filter.dictionaryLabel]}
									isFullColor={filter.isFullColor}
									isSelected={filter.key === activeFilter?.key}
									amount={selectedOptions.length}
									showCaret={true}
									onClick={() => handleFilterClick(filter)}
									rootClass={isPinnedSlim ? '' : styles.dropdown}
									badgeClass={styles.badge}
									selectedOptions={selectedOptions}
									useSelectionAsLabel={true}
									variant={isPinnedSlim ? FilterDropdownVariant.Slim : FilterDropdownVariant.Regular}
									innerRef={isNumberOfGuestsFilter(filter) ? numberOfGuestsFilterRef : undefined}
								/>
							</div>
						);
					})}
				</div>
				<BasicButton
					testId="widget-cruises-search-button"
					label={isPinnedRegular ? '' : actionSearchCruises}
					variant={'fullColor'}
					onClick={handleSubmitClick}
					icon={isPinnedRegular ? SearchIcon : undefined}
					customClass={isPinnedRegular ? styles.searchIcon : styles.search}
				/>
			</div>

			{showFiltersModal && isNumberOfGuestsFilter(activeFilter) && (
				<NumberOfGuestsModal filterDropdownElement={numberOfGuestsFilterRef?.current} isPinned={isPinned} scrollingContainer={scrollingContainer} />
			)}
		</div>
	);
}
